import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Variables } from "../../Variations/Variations";
import { toast } from "react-toastify";
import { AiOutlineRight } from "react-icons/ai";

const ManageProducts = () => {
  const { ProductID } = useParams();
  const navigate = useNavigate();
  const productIdNum = Number(ProductID) || 0;

  // ---- State (matches API JSON) ----
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [body, setBody] = useState("");
  const [bodyAr, setBodyAr] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttonTextAr, setButtonTextAr] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [categoryId, setCategoryId] = useState(null); // number|null

  // ---- Categories for select ----
  const [categories, setCategories] = useState([]);
  const categoryOptions = (Array.isArray(categories) ? categories : []).map((x) => ({
    value: x.Id,
    label: x.Name ?? String(x.Id),
  }));
  const selectedCategoryOption =
    categoryOptions.find((o) => o.value === categoryId) || null;

  // ---- Refs ----
  const fileInputRef = useRef(null);
  const isBlank = (v) => typeof v !== "string" || v.trim() === "";

  // ---- Loaders ----
  async function loadCategories() {
    try {
      const res = await fetch(`${Variables.API_URL}Rasha/Category/Get`);
      if (!res.ok) throw new Error();
      const result = await res.json();
      console.log(result)
      // Normalize if wrapped
      const flat = (result || []).map((x) => ({
        Id: x?.Category?.Id ?? x?.Id ?? 0,
        Name: x?.Category?.Name ?? x?.Name ?? "",
        _raw: x,
      }));
      setCategories(flat);
    } catch {
      toast.error("Failed to load categories");
    }
  }

  async function loadProduct() {
    try {
      const res = await fetch(`${Variables.API_URL}Rasha/Product/Get/${productIdNum}`);
      if (!res.ok) return;
      const data = await res.json();
      console.log(JSON.stringify(data));

      const p = data.Product ?? data.product ?? data ?? {};

      setName(p.name ?? p.Name ?? "");
      setNameAr(p.nameAr ?? p.NameAr ?? "");
      setBody(p.body ?? p.Body ?? "");
      setBodyAr(p.bodyAr ?? p.BodyAr ?? "");
      setButtonUrl(p.buttonUrl ?? p.ButtonUrl ?? "");
      setButtonText(p.buttonText ?? p.ButtonText ?? "");
      setButtonTextAr(p.buttonTextAr ?? p.ButtonTextAr ?? "");
      setImageUrl(p.imageUrl ?? p.ImageUrl ?? "");
      setCategoryId(p.categoryId ?? p.CategoryId ?? null);
      setIsPublished(
        typeof p.isPublished === "boolean" ? p.isPublished : Boolean(p.IsPublished)
      );
    } catch {
      toast.error("Failed to load Product");
    }
  }

  useEffect(() => {
    loadCategories();
    if (productIdNum !== 0) loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdNum]);

  // ---- Normal <input type="file"> upload (<= 2MB) ----
  const onFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size >= 2_000_000) {
      toast.error("Image is not accepted (max 2MB).");
      e.target.value = "";
      return;
    }

    const formdata = new FormData();
    formdata.append("Images", file);

    const requestOptions = { method: "POST", body: formdata, redirect: "follow" };

    fetch(`${Variables.API_URL}UploadMedia/UploadImageToServer`, requestOptions)
      .then((response) => response.json())
      .then((result) => setImageUrl(result?.[0] ?? ""))
      .catch((error) => console.error(error));
  };

  // ---- Validation ----
  function isValid() {
    if (isBlank(name)) {
      toast.error("Name is required");
      return false;
    }
    if (isBlank(body)) {
      toast.error("Body is required");
      return false;
    }
    if (isBlank(buttonText)) {
      toast.error("Button Text is required");
      return false;
    }
    if (isBlank(buttonUrl)) {
      toast.error("Button URL is required");
      return false;
    }
    if (isBlank(imageUrl)) {
      toast.error("Image is required (upload an image)");
      return false;
    }
    if (!categoryId || Number(categoryId) <= 0) {
      toast.error("Category is required");
      return false;
    }
    if (isBlank(nameAr)) {
      toast.error("Name (Arabic) is required");
      return false;
    }
    if (isBlank(bodyAr)) {
      toast.error("Body (Arabic) is required");
      return false;
    }
    if (isBlank(buttonTextAr)) {
      toast.error("Button Text (Arabic) is required");
      return false;
    }
    return true;
  }

  // ---- Save (Add / Update) ----
  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      const payload = {
        id: productIdNum === 0 ? undefined : productIdNum,
        name,
        nameAr,
        body,
        bodyAr,
        buttonUrl,
        buttonText,
        buttonTextAr,
        isPublished,
        categoryId,
        imageUrl,
      };
      console.log(payload);

      const res = await fetch(`${Variables.API_URL}Rasha/Product/Upsert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      toast.success(
        productIdNum === 0 ? "Product added successfully" : "Product updated successfully"
      );
      navigate("/admin/rasha/Products");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  }

  // ---- Render ----
  return (
    <div className="rightSide">
      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <h2>
              {productIdNum === 0 ? "Add" : `Manage ${name || "Product"}`} Product
            </h2>
            <ul className="portalPath">
              <li>
                Home <AiOutlineRight />
              </li>
              <li>
                <Link to="/admin/rasha/Products">
                  Products <AiOutlineRight />
                </Link>
              </li>
              <li>{productIdNum === 0 ? "Add" : "Manage"} Product</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <NavLink to="/admin/rasha/Products" className="btn btn-secondary me-2">
                    Cancel
                  </NavLink>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Save
                  </button>
                </div>

                <div className="row gy-3">
                  {/* Name / NameAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Name <span className="required">*</span>
                    </label>
                    <input
                      id="name"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Name (Arabic)</label>
                    <input
                      id="nameAr"
                      className="form-control"
                      value={nameAr}
                      onChange={(e) => setNameAr(e.target.value)}
                      placeholder="Name in Arabic"
                    />
                  </div>

                  {/* Body / BodyAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">Body</label>
                    <textarea
                      id="body"
                      className="form-control"
                      rows={5}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Product body"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Body (Arabic)</label>
                    <textarea
                      id="bodyAr"
                      className="form-control"
                      rows={5}
                      value={bodyAr}
                      onChange={(e) => setBodyAr(e.target.value)}
                      placeholder="Product body in Arabic"
                    />
                  </div>

                  {/* Button fields */}
                  <div className="col-lg-6">
                    <label className="mb-2">Button Text</label>
                    <input
                      id="buttonText"
                      className="form-control"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder="e.g., Learn more"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Button Text (Arabic)</label>
                    <input
                      id="buttonTextAr"
                      className="form-control"
                      value={buttonTextAr}
                      onChange={(e) => setButtonTextAr(e.target.value)}
                      placeholder="Arabic button text"
                    />
                  </div>

                  <div className="col-lg-12">
                    <label className="mb-2">Button URL</label>
                    <input
                      id="buttonUrl"
                      className="form-control"
                      value={buttonUrl}
                      onChange={(e) => setButtonUrl(e.target.value)}
                      placeholder="Button Url"
                    />
                  </div>

                  {/* Category select */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Category <span className="required">*</span>
                    </label>
                    <Select
                      options={categoryOptions}
                      value={selectedCategoryOption}
                      onChange={(opt) => setCategoryId(opt ? opt.value : null)}
                      placeholder="Select a category…"
                      isClearable
                    />
                  </div>

                  {/* Publish toggle */}
                  <div className="form-group col-lg-3 published-check d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="publish"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                    &nbsp;
                    <label className="form-check-label" htmlFor="publish">
                      Publish
                    </label>
                  </div>

                  {/* Image uploader (normal input) */}
                  <div className="col-lg-6">
                    <label className="mb-2">Image (max 2MB)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={onFileInputChange}
                    />
                    {imageUrl && (
                      <div className="mt-3">
                        <img
                          src={Variables.API_PUBLISHED + imageUrl}
                          alt="preview"
                          style={{ maxWidth: "100%", maxHeight: 200, display: "block" }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger mt-2"
                          onClick={() => setImageUrl("")}
                        >
                          Remove Image
                        </button>
                        <small className="text-muted d-block mt-2">
                          Uploaded URL: {imageUrl}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default ManageProducts;
